import type { AmplifyUser } from '@aws-amplify/ui'
import { Auth } from 'aws-amplify'
import {
    clearAmplifyStore,
    getAmplifyStoreUser,
    setAmplifyStoreUser,
    useAmplifyStoreUser,
} from '../actions/amplifyUser.store'
import { startAmplifyOnce } from '../utils'

export interface User {
    username?: string
    email?: string
    name?: string
    familyName?: string
    amplifyUser?: AmplifyUser
}

export const parseAmplifyUser = (u: AmplifyUser): User => {
    // username: 'foo'
    // attributes: {
    //   custom:pipeline_ids: '["166aa","abcstl",...]'
    //   custom:selected_pipeline_id: 'daanw'
    //   custom:spark_bi_roles: 'ROLE_SPARK_REPORTING,...'
    //   email: 'foo@example.com'
    //   email_verified: true
    //   family_name: 'Doe'
    //   name: 'John'
    //   sub: '00000000-0000-0000-0000-000000000000'
    // }

    return {
        username: u.username,
        email: u.attributes?.email,
        name: u.attributes?.['name'],
        familyName: u.attributes?.['family_name'],
        amplifyUser: u,
    }
}

/** Uses custom store cache first, then uses amplify's `Auth.currentAuthenticatedUser` rather than `useAuthenticator`
 *
 * Issue: Currently only listens to signOut events [amplify-ui #1497](https://github.com/aws-amplify/amplify-ui/issues/1497#issuecomment-1115188487)
 */
export const useCurrentAuthenticatedUser = () => {
    const storeUser = useAmplifyStoreUser()

    if (storeUser) {
        const parsedUser = parseAmplifyUser(storeUser)
        // console.debug('useCurrentAuthenticatedUser (cached)', parsedUser)
        return parsedUser
    }

    setTimeout(async () => {
        await startAmplifyOnce()

        try {
            const amplifyUser = await Auth.currentAuthenticatedUser()
            if (amplifyUser) {
                setAmplifyStoreUser(amplifyUser)
                const parsedUser = parseAmplifyUser(amplifyUser)
                console.debug('useCurrentAuthenticatedUser (pending)', parsedUser)
            }
        } catch (e) {
            console.debug('useCurrentAuthenticatedUser (error)', e)
            clearAmplifyStore()
        }
    }, 0)

    return null
}

/** Uses custom store cache first, then uses amplify's `Auth.currentAuthenticatedUser`
 *
 * Issue: Currently only listens to signOut events [amplify-ui #1497](https://github.com/aws-amplify/amplify-ui/issues/1497#issuecomment-1115188487)
 */
export const getCurrentAuthenticatedUser = async () => {
    const storeUser = getAmplifyStoreUser()

    if (storeUser) {
        const parsedUser = parseAmplifyUser(storeUser)
        // console.debug('getCurrentAuthenticatedUser (cached)', parsedUser)
        return parsedUser
    }

    try {
        await startAmplifyOnce()
        const amplifyUser = await Auth.currentAuthenticatedUser()
        if (amplifyUser) {
            setAmplifyStoreUser(amplifyUser)
            const parsedUser = parseAmplifyUser(amplifyUser)
            console.debug('getCurrentAuthenticatedUser (pending)', parsedUser)
            return parsedUser
        }
    } catch (e) {
        console.debug('getCurrentAuthenticatedUser (error)', e)
        clearAmplifyStore()
    }

    return null
}
